import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api, { API } from "../lib/api";
import Seo from "../components/Seo";

const thumbUrl = (id, size = 720) => `${API}/public/photos/${id}/image.jpg?size=${size}`;
const fullUrl = (id) => `${API}/public/photos/${id}/image.jpg?size=1600&crop=0`;

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  const [museumFrame, setMuseumFrame] = useState(false);

  useEffect(() => {
    api.get("/public/gallery").then((r) => setPhotos(r.data)).catch((e) => console.warn("[gallery] load failed:", e));
    api.get("/public/settings").then((r) => setMuseumFrame(!!r.data.gallery_museum_frame)).catch(() => {});
  }, []);

  return (
    <div data-testid="gallery-page" className={museumFrame ? "museum-wrapper" : ""}>
      <Seo path="/gallery" title="Student gallery — paintings from our East Croydon class"
        description="A gallery of paintings, drawings and studies by students at Seasons Art Class East Croydon — watercolours, oils, acrylics and drawing." />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <div className="font-hand text-[#A685BD] text-2xl">paintings from our students</div>
        <h1 className="font-display text-5xl md:text-6xl text-[#5D2C8F] mt-2 brush-underline">Gallery</h1>
        <p className="mt-6 text-[#5A4A6B] max-w-xl leading-relaxed">
          A little peek inside our studio — finished pieces, works-in-progress, and joyful moments from class.
        </p>
      </section>
      <section className={`max-w-7xl mx-auto px-6 lg:px-10 pb-24 ${museumFrame ? "museum-hall pt-16" : ""}`}>
        {photos.length === 0 && (
          <div className="soft-card p-10 text-center">
            <div className="font-display text-2xl text-[#5D2C8F]">Photos coming soon.</div>
            <p className="text-sm text-[#6E6074] mt-2">Upload photos in <span className="font-mono text-[#5D2C8F]">/admin/social</span> and tick "Publish to gallery" to see them here.</p>
          </div>
        )}

        {museumFrame ? (
          /* --- Museum hanging-frame layout --- */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-20 pt-8">
            {photos.map((p, i) => (
              <figure
                key={p.id}
                className="museum-piece fade-up flex flex-col items-center"
                style={{ animationDelay: `${(i % 9) * 80}ms` }}
                data-testid={`gallery-item-${p.id}`}
              >
                <div className="museum-cord" aria-hidden />
                <button
                  onClick={() => setActive(p)}
                  className="museum-frame block focus:outline-none focus:ring-2 focus:ring-[#E8C37A] rounded-sm"
                  aria-label={p.alt_text || "View artwork"}
                >
                  <div className="museum-mat">
                    <img
                      src={thumbUrl(p.id)}
                      alt={p.alt_text || "Art class piece"}
                      loading="lazy"
                      className="museum-art"
                    />
                  </div>
                </button>
                {p.alt_text && <figcaption className="museum-plaque">{p.alt_text}</figcaption>}
              </figure>
            ))}
          </div>
        ) : (
          /* --- Default masonry layout --- */
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
            {photos.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActive(p)}
                className="block w-full rounded-2xl overflow-hidden fade-up"
                style={{ animationDelay: `${(i % 9) * 60}ms` }}
                data-testid={`gallery-item-${p.id}`}
              >
                <img src={thumbUrl(p.id)} alt={p.alt_text || "Art class photo"} loading="lazy" className="w-full object-cover hover:scale-[1.02] transition-transform" />
              </button>
            ))}
          </div>
        )}
      </section>
      {active && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-6" onClick={() => setActive(null)}>
          <button className="absolute top-6 right-6 text-white p-2 rounded-full bg-white/10 hover:bg-white/20" onClick={() => setActive(null)}><X /></button>
          <img src={fullUrl(active.id)} alt={active.alt_text || ""} className="max-h-[88vh] max-w-full rounded-xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Museum-frame styles (only loaded when flag is on) */}
      {museumFrame && (
        <style>{`
          .museum-wrapper { background: linear-gradient(180deg, #F4EDDD 0%, #EFE4D1 100%); }
          .museum-hall { position: relative; }
          /* Faint ceiling rail */
          .museum-hall::before {
            content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px;
            background: linear-gradient(90deg, transparent, #8B7355 20%, #6B5842 50%, #8B7355 80%, transparent);
            opacity: 0.35;
          }
          .museum-piece { position: relative; padding-top: 42px; }
          .museum-cord {
            position: absolute; top: 0; left: 50%; width: 2px; height: 44px;
            background: linear-gradient(180deg, #6B5842, #8B7355);
            transform: translateX(-50%);
            box-shadow: 0 0 3px rgba(0,0,0,0.15);
          }
          .museum-cord::before {
            content: ""; position: absolute; top: -3px; left: -3px;
            width: 8px; height: 8px; border-radius: 50%; background: #5A4A37;
          }
          .museum-frame {
            background: linear-gradient(145deg, #8B6F47 0%, #A88556 35%, #6B4F2E 100%);
            padding: 14px;
            border-radius: 4px;
            box-shadow:
              0 2px 0 rgba(0,0,0,0.1),
              0 8px 20px rgba(0,0,0,0.25),
              inset 0 0 0 3px #DBB57A,
              inset 0 0 0 6px #6B4F2E;
            transform: perspective(900px) rotateX(0deg);
            transition: transform 0.35s ease, box-shadow 0.35s ease;
          }
          .museum-frame:hover {
            transform: perspective(900px) rotateX(-1.5deg) translateY(-3px);
            box-shadow:
              0 2px 0 rgba(0,0,0,0.12),
              0 16px 30px rgba(0,0,0,0.32),
              inset 0 0 0 3px #DBB57A,
              inset 0 0 0 6px #6B4F2E;
          }
          .museum-mat {
            background: #FBF7EE;
            padding: 18px;
            box-shadow: inset 0 0 0 1px #D9CFB3, inset 0 1px 4px rgba(0,0,0,0.08);
          }
          .museum-art {
            display: block; width: 100%; height: auto; max-height: 340px; object-fit: cover;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          }
          .museum-plaque {
            margin-top: 16px;
            padding: 6px 14px;
            background: #B89968;
            color: #2C1F10;
            font-family: 'Fraunces', Georgia, serif;
            font-size: 12px;
            font-style: italic;
            letter-spacing: 0.04em;
            border-radius: 2px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3);
            max-width: 240px;
            text-align: center;
          }
        `}</style>
      )}
    </div>
  );
}
